import { useEffect, useState, useContext } from 'react';
import { RiFileTextFill } from 'react-icons/ri';
import { MdVideoLibrary } from 'react-icons/md';
import { FaUsers } from 'react-icons/fa';
import { AppStateContext } from '../../../state/AppProvider';
import { resolveEntities } from '../../../api/api';
import { AssistantFile, Assistant, KnowledgeFileType, User, AzureGroup } from '../../../api/models';
import '../ui/AssistantBuilder.css';

interface AssistantViewerProps {
    id: string;
    onCancel: () => void;
}

export const AssistantViewer = ({ id, onCancel }: AssistantViewerProps) => {
    const appStateContext = useContext(AppStateContext);
    const [title, setTitle] = useState('');
    const [promptMessage, setPromptMessage] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [assistantFiles, setAssistantFiles] = useState<AssistantFile[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [groups, setGroups] = useState<AzureGroup[]>([])
    const [managerUsers, setManagerUsers] = useState<User[]>([]);
    const [managerGroups, setManagerGroups] = useState<AzureGroup[]>([]);
    const [creatorId, setCreatorId] = useState('');
    const [assistantUrl, setAssistantUrl] = useState('');
    const [tools, setTools] = useState<string[]>([]);
    const [toolDefaults, setToolDefaults] = useState<string[]>([]);

    useEffect(() => {
        const fetchAssistantDetails = async () => {
            const assistants = appStateContext?.state.assistants || []
            const a = assistants.find((x: Assistant) => x.id === id)
            if (!a) return

            setTitle(a.title)
            setPromptMessage(a.system_prompt_message)
            setWelcomeMessage(a.welcome_message)
            setAssistantFiles(a.files || [])
            setAssistantUrl(a.url || '')
            setTools(a.tools || [])
            setToolDefaults(a.tool_defaults || [])

            const uids = a.userIds ?? []
            const gids = a.groups ?? []
            if (uids.length > 0 || gids.length > 0) {
                try {
                    const ents = await resolveEntities(uids, gids)
                    setUsers(ents.filter((e): e is User => e.objectType === 'user'))
                    setGroups(ents.filter((e): e is AzureGroup => e.objectType === 'group'))

                } catch (e) {
                    console.error('Error resolving entities', e)
                }
            }

            setCreatorId(a.created_by)
            const managerUids = [a.created_by, ...(a.managerIds ?? [])].filter(Boolean)
            const managerGids = a.managerGroups ?? []
            try {
                const managerEnts = await resolveEntities(managerUids, managerGids)
                setManagerUsers(managerEnts.filter((e): e is User => e.objectType === 'user'))
                setManagerGroups(managerEnts.filter((e): e is AzureGroup => e.objectType === 'group'))
            } catch (e) {
                console.error('Error resolving entities', e)
            }
        }

        fetchAssistantDetails()
    }, [id, appStateContext])

    const getFileType = (fileName: string): KnowledgeFileType => {
        const lowerName = fileName?.toLowerCase() || '';
        if (lowerName.endsWith('.mp4')) return 'video';
        return 'document';
    };

    const formatFileSize = (size: number) => {
        if (size >= 1024 * 1024 * 1024) {
            return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        } else if (size >= 1024 * 1024) {
            return (size / (1024 * 1024)).toFixed(2) + ' MB';
        } else if (size >= 1024) {
            return (size / 1024).toFixed(2) + ' KB';
        } else {
            return size + ' B';
        }
    };

    const documentCount = assistantFiles.filter(f => f?.fileName && getFileType(f.fileName) === 'document').length;
    const videoCount = assistantFiles.filter(f => f?.fileName && getFileType(f.fileName) === 'video').length;

    return (
        <div className="relative flex flex-col h-full min-w-0">
            <div className="flex-grow min-w-0 overflow-y-auto overflow-x-hidden p-4">
                <div className="border-b border-gray-200 pb-5 mb-8">
                    <h2 className="mt-1 text-2xl font-semibold text-jtc-very-dark-grey break-words">
                        {title}
                    </h2>
                    {assistantUrl ? (
                        <a
                            href={`https://chat.jtcgroup.ai/${assistantUrl}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-block text-sm text-jtc-blue hover:underline break-all"
                        >
                            https://chat.jtcgroup.ai/{assistantUrl}
                        </a>
                    ) : (
                        <p className="mt-1 text-sm text-gray-500">No URL set.</p>
                    )}
                </div>

                <div className="mb-8">
                    <h4 className="text-lg font-semibold text-jtc-very-dark-grey">
                        Prompt Message
                    </h4>
                    <p className="text-sm mb-2 text-gray-500">
                        Instructions that define how this assistant behaves.
                    </p>
                    <div className="rounded-md border border-jtc-grey bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap break-words">
                        {promptMessage}
                    </div>
                </div>

                <div className="mb-8">
                    <h4 className="text-lg font-semibold text-jtc-very-dark-grey">
                        Welcome Message
                    </h4>
                    <p className="text-sm mb-2 text-gray-500">
                        Shown at the start of every new conversation.
                    </p>
                    {welcomeMessage ? (
                        <div className="rounded-md border border-jtc-grey bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap break-words">
                            {welcomeMessage}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic">No welcome message set.</p>
                    )}
                </div>

                {/* Tools */}
                <div className="mb-8">
                    <h4 className="text-lg font-semibold text-jtc-very-dark-grey">
                        Tools
                    </h4>
                    <p className="text-sm mb-2 text-gray-500">
                        Extra capabilities enabled for this assistant.
                    </p>
                    {tools.length > 0 ? (
                        <div className="flex flex-wrap gap-3">
                            {tools.includes('reasoning') && (
                                <span className="flex items-center gap-2 px-4 py-2 rounded-md border border-jtc-blue bg-jtc-blue/10 text-jtc-blue text-sm font-medium">
                                    <span className="material-symbols-outlined text-[18px] leading-none">psychology</span>
                                    Thinking
                                    {toolDefaults.includes('reasoning') && (
                                        <span className="text-xs opacity-70">on by default</span>
                                    )}
                                </span>
                            )}
                            {tools.includes('web_search') && (
                                <span className="flex items-center gap-2 px-4 py-2 rounded-md border border-jtc-blue bg-jtc-blue/10 text-jtc-blue text-sm font-medium">
                                    <span className="material-symbols-outlined text-[18px] leading-none">captive_portal</span>
                                    Web Search
                                    {toolDefaults.includes('web_search') && (
                                        <span className="text-xs opacity-70">on by default</span>
                                    )}
                                </span>
                            )}
                            {tools.includes('file_generation') && (
                                <span className="flex items-center gap-2 px-4 py-2 rounded-md border border-jtc-blue bg-jtc-blue/10 text-jtc-blue text-sm font-medium">
                                    <span className="material-symbols-outlined text-[18px] leading-none">note_add</span>
                                    File Generation
                                </span>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic">No tools enabled.</p>
                    )}
                </div>

                <div className="mb-8">
                    <h4 className="text-lg font-semibold text-jtc-very-dark-grey">
                        Documents & Videos
                    </h4>
                    <p className="text-sm mb-2 text-gray-500">
                        Files attached to this assistant
                        {documentCount > 0 && ` (${documentCount} document${documentCount > 1 ? 's' : ''})`}
                        {videoCount > 0 && ` (${videoCount} video${videoCount > 1 ? 's' : ''})`}.
                    </p>
                    {assistantFiles.length > 0 ? (
                        <ul className="rounded-md border border-jtc-grey divide-y divide-jtc-grey overflow-hidden">
                            {assistantFiles.map((file, index) => {
                                const fileType = getFileType(file.fileName);
                                return (
                                    <li key={index} className="flex items-center gap-3 bg-white px-3 py-2 min-w-0">
                                        {fileType === 'document' ? (
                                            <RiFileTextFill className="text-jtc-blue flex-shrink-0" />
                                        ) : (
                                            <MdVideoLibrary className="text-jtc-purple flex-shrink-0" />
                                        )}
                                        <span className="flex-1 min-w-0 text-sm text-gray-700 break-all">{file.fileName}</span>
                                        {file.fileSize && (
                                            <span className="flex-shrink-0 text-xs text-gray-400">
                                                {formatFileSize(file.fileSize)}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500 italic">No documents or videos attached.</p>
                    )}
                </div>
            </div>

            <div className="border-t border-gray-200 p-4 bg-white flex justify-end gap-4">
                <button
                    className="jtc-secondary-button transition hover:bg-jtc-secondary-hover"
                    onClick={onCancel}
                >
                    Back
                </button>
            </div>
        </div>
    );
};
